import React, { useEffect, useMemo } from "react";
import { IBundleItem, IRoute, IRouteLeg, getApiCallState, useStateSelector } from "../Models";
// import { getStrings } from "../../Shared/Reservation/StringResources";
import { useDispatch } from "react-redux";
import { bookTemporaryTable, clearTemporaryTable, loadAvailableTimes } from "../Actions";
import { Badge, Button, Col, Row } from "react-bootstrap";
import { Price } from "../Components/Price";
import { NumberSelector } from "../Components/NumberSelector";
import dayjs from "dayjs";
import { getStrings } from "../../Shared/Reservation/StringResources";
import SvgInfoCircleFill from "../../Shared/Icons/bootstrap/InfoCircleFill";
import SvgExclamationTriangleFill from "../../Shared/Icons/bootstrap/ExclamationTriangleFill";
import { isTest } from "../S3BookingApp";
import { IKrogenSlot, getOptionsByLegAndSlot } from "./KrogenOptionsHelpers";

const strings = getStrings().component.tableBooking;
const commonStrings = getStrings().component.common;
const optionStrings = getStrings().component.options;

export const KrogenOptions = (props: { options: IBundleItem[], route: IRoute, getOptionCount: (leg: IRouteLeg, option: IBundleItem) => number, onOptionChanged: (option: IBundleItem, count: number, legId: string) => void }) =>
{
	const optionsByLegAndSlot = useMemo(() => getOptionsByLegAndSlot(props.options, props.route.legs),
		[props.options?.map(opt => opt.id).join(","), props.route?.id]);

	const getTableBookingCount = (leg: IRouteLeg, options: IBundleItem[]) => {
		let count = 0;
		options.forEach(opt => {
			count += props.getOptionCount(leg, opt);
		});

		const max = options[0].passengerFares.length; //Samma på alla, vi kan titta på vilken som helst

		return Math.min(count, max);
	};

	return <section>
		<h2>{optionStrings.optionGroups.krogen}</h2>

		{props.route.legs.filter(leg => optionsByLegAndSlot.some(x => x.legId === leg.id)).map(leg => <div key={leg.id}>
			{ props.route.legs.length > 1 && <h4 className="mb-2" style={{ textDecoration: "underline" }}>{leg.departureStation.name} - {leg.arrivalStation.name}</h4> }

			<div className="option-selector">
				{optionsByLegAndSlot.filter(x => x.legId === leg.id).map(g => <div key={g.slot.slotName}>
					{optionsByLegAndSlot.filter(x => x.legId === leg.id).length > 1 &&
						<strong>{strings.slots[g.slot.slotName]}{optionsByLegAndSlot.some(x => x.slot.departureDayOffset > 0) ? `, ${g.day?.format("D MMM")}`  : null} {isTest() && <Badge variant="light" className="ml-4">{g.slot.startTime}-{g.slot.endTime}</Badge>}</strong>
					}

					{ g.options.map(opt => <React.Fragment key={opt.id}>
						<Row key={opt.id} className="mb-1">
							<Col xs={8} sm={9}><label htmlFor={`option_${opt.id}`}>{opt.name} (<Price amount={opt.passengerFares[0].price} />){isTest() && <Badge variant="light" className="ml-4">{opt.productCode}</Badge>}</label></Col>
							<Col xs={4} sm={3}><NumberSelector inputId={`option_${opt.id}`} min={0} max={opt.passengerFares.length} initialValue={props.getOptionCount(leg, opt)} onValueChanged={n => props.onOptionChanged(opt, n, leg.id)} /></Col>
						</Row>
					</React.Fragment>)}

					<KrogenBokning leg={leg} option={g.options[0]} count={getTableBookingCount(leg, g.options)} slot={g.slot} />
				</div>)}
			</div>
		</div>)}


	</section>
}


const KrogenBokning = (props: { leg: IRouteLeg, option: IBundleItem, count: number, slot: IKrogenSlot }) => {

	const dispatch = useDispatch();

	const tableBookings = useStateSelector(state => state.travelDetails.tableBookings);
	const numberOfPassengers = useStateSelector(state => state.search?.passengers?.length)
	const availableTimes = tableBookings?.availableTimes?.hasOwnProperty(props.leg.id) ? tableBookings.availableTimes[props.leg.id].find(x => x.productCode === props.option.productCode) : null;

	const currentBooking = tableBookings?.bookings?.find(x => x.legId === props.leg.id && x.productCode === props.option.productCode);
	const currentBookingDate = currentBooking?.date ? dayjs(currentBooking.date) : null;
	const isNightTrain = !dayjs(props.leg.departureStation.departureTimestamp).isSame(props.leg.arrivalStation.arrivalTimestamp, "day");

	const bookTableState = getApiCallState().getLoadingState("bookTemporaryTable");

	const selectTime = (time: string) => {
		console.debug("selectTime", time, availableTimes.date);
		dispatch(bookTemporaryTable(availableTimes.date, time, props.leg.serviceName, props.count, props.option.productCode, props.leg.id, props.slot.slotName));
	};

	useEffect(() => {
		if (props.count > 0)
			dispatch(loadAvailableTimes(props.leg, props.count, props.option.productCode, props.slot.startTime, props.slot.endTime, props.slot.departureDayOffset))
	}, [props.leg.id, props.option.productCode, props.count]);

	useEffect(() => {
		if (currentBooking && currentBooking.persons !== props.count)
			dispatch(clearTemporaryTable(currentBooking.bookingId));
	}, [props.count]);

	if (currentBooking)
		return <p className="text-right">
			{strings.tableBooked} <strong>{currentBooking.time}{isNightTrain ? `, ${currentBookingDate.format("D MMM")}` : ""} {numberOfPassengers > 1 && `(${currentBooking.persons} ${currentBooking.persons === 1 ? commonStrings.person : commonStrings.persons})`}</strong>
			<Button variant="link" className="ml-2" onClick={() => dispatch(clearTemporaryTable(currentBooking.bookingId))}>{strings.changeTime}</Button>
		</p>

	if (props.count === 0)
		return <p className="text-right muted"><SvgInfoCircleFill /> {strings.bookingRequired}</p>;

	if (!availableTimes)
		return <p className="text-right muted">{strings.fetchingTables}</p>;

	if (availableTimes?.times.length === 0)
		return <p className="text-right danger"><SvgExclamationTriangleFill /> {strings.noAvailableTimes}</p>;

	return <div className="availableTimes mt-3 mb-3">
		<label>{strings.availableTimes}</label><br />
		{availableTimes.times.map(x => <Button disabled={bookTableState === "loading"} variant="outline-primary" size="sm" className="mr-1 mb-2" key={x} onClick={() => selectTime(x)}>{x}</Button>)}
	</div>
};